<script setup lang="ts">
const { isAuthenticated } = useAuth()
const { i18nEnabled } = useTenantConfig()
const appConfig = useAppConfig()

const headerVariant = computed(() => appConfig.theme.header.variant)

// Enable the language toggle in the header?
// We should only show this when the tenant has i18n enabled AND the appconfig is configured to show the language toggle in the header.
const showLanguageToggle = computed(() => i18nEnabled && appConfig.theme.header.languageToggle)

const cmsMenuPosition = computed(() => headerVariant.value == 'tab' ? 'top' : 'bottom')
const isSticky = computed(() => appConfig.theme.header.sticky)
const backdropBlur = computed(() => isSticky.value)

// TODO: make appConfig variable for this
const bgOpacity = ref(0.90) // Adjustable opacity value (0-1)

const headerBgStyle = ref('')

// This is extremely hacky, but i wasn't able to make it work.
// this works: bg-[var(--color-theme-background)]
// but this not: bg-[var(--color-theme-background) / 0.95]
// that's why I created this.
// Note: this only works on the client, that's why we call it from onMounted
function updateHeaderBgStyle() {
  console.log(`updateHeaderBgStyle()`)
  try {
    const bgColor = getComputedStyle(document.documentElement)
      .getPropertyValue(colorMode.value == 'dark' ? '--color-theme-background-dark' : '--color-theme-background')
      .trim()

    console.log(`bgColor:`, bgColor)

    // Parse hex code
    if (bgColor.includes('#')) {
      // Convert hex to rgba
      let hex = bgColor.replace('#', '')

      // Handle shorthand hex
      if (hex.length === 3) {
        hex = hex.split('').map(c => c + c).join('')
      }

      // Convert to RGB
      const r = parseInt(hex.substring(0, 2), 16)
      const g = parseInt(hex.substring(2, 4), 16)
      const b = parseInt(hex.substring(4, 6), 16)

      headerBgStyle.value = `background-color: rgba(${r}, ${g}, ${b}, ${bgOpacity.value})`
    }
    // Parse rgb code
    else if (bgColor.includes('rgb')) {
      const r = bgColor.split(' ')[0].replace('rgb(', '').replace(')', '')
      const g = bgColor.split(' ')[1].replace(')', '')
      const b = bgColor.split(' ')[2].replace(')', '')
      headerBgStyle.value = `background-color: rgba(${r}, ${g}, ${b}, ${bgOpacity.value})`
    }
    else {
      console.error(`Unexpected value for bgColor in Header.vue:`, bgColor)
      const fallbackColor = colorMode.value == 'dark' ? 'rgba(0.0, 0.0, 0.0, 0.7)' : 'rgba(255, 255, 255, 0.9)'
      headerBgStyle.value = `background-color: ${fallbackColor};`
    }

    console.log(`headerBgStyle.value`, headerBgStyle.value)
  }
  catch (error) {
    console.error('Error computing background style:', error)
  }
}

// Note: colorMode has issues with reactivity, so we need to watch it
const colorMode = useColorMode()
watch(colorMode, () => updateHeaderBgStyle())
onMounted(() => updateHeaderBgStyle())

// Always apply side padding for now, since we also do this in the content container(s)
// The padding should be the same padding as in ContentBlockViewPartialContainer
const hasSidePadding = true

// Dynamic header height calculation
const headerRef = ref<HTMLElement | null>(null)
const headerHeight = ref(0)

function updateHeaderHeight() {
  if (headerRef.value) {
    headerHeight.value = headerRef.value.offsetHeight
  }
}

onMounted(() => {
  updateHeaderHeight()
  window.addEventListener('resize', updateHeaderHeight)
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', updateHeaderHeight)
})

// Watch for changes that might affect the header height
watch([isAuthenticated, cmsMenuPosition], () => {
  // Use nextTick to ensure the DOM has updated
  nextTick(updateHeaderHeight)
  // To be sure, do another check after 100ms, mainly to fix some delayed layout changes after sign in.
  setTimeout(updateHeaderHeight, 100)
})
</script>

<template>
  <header
    ref="headerRef"
    :style="headerBgStyle"
    :class="{
      'px-4': hasSidePadding,
      'backdrop-blur-sm': backdropBlur,
      'fixed inset-x-0 top-0 z-50 w-full border-b border-[var(--color-theme-header-border)] ': isSticky,
      'bg-theme-background': !backdropBlur,
      'border-b border-[var(--color-theme-header-border)]': true,
    }"
  >
    <Container
      class="py-3"
      :apply-padding="false"
      :full-width="appConfig.theme.header.fullWidth"
    >
      <!-- Desktop layout using grid -->
      <div class="hidden items-center justify-between gap-4 md:flex">
        <!-- Logo container -->
        <!-- 'mb-4': !isAuthenticated && cmsMenuPosition === 'top', -->
        <div
          class="flex h-16 min-w-32 items-center gap-x-4"
        >
          <NuxtLink to="/">
            <PublicLogo max-height-class="max-h-12" />
          </NuxtLink>
        </div>

        <!-- Toolbar container -->
        <div class="flex items-center justify-end gap-3">
          <PublicCmsMenu v-if="cmsMenuPosition === 'top'" />
          <ToolbarLanguage v-if="showLanguageToggle" />
          <ColorModeToggle />
          <ToolbarAccountMenu v-if="isAuthenticated" />
          <ToolbarSignIn v-else />
        </div>
      </div>

      <!-- Mobile layout -->
      <div class="flex items-center justify-between gap-2 md:hidden">
        <!-- We had `class="-ml-2 mt-1"` here but I removed it for Roermond -->
        <SidebarBurger />

        <NuxtLink to="/" class="flex items-end justify-end">
          <PublicLogo class="max-h-14 max-w-[80%]" />
        </NuxtLink>
      </div>
    </Container>

    <PublicCmsMenu v-if="cmsMenuPosition === 'bottom'" />

    <PublicAuthenticatedMenu v-if="isAuthenticated" />
  </header>

  <!-- Spacer to prevent content from being hidden under the fixed sticky header -->
  <div
    v-if="isSticky"
    :style="{ height: `${headerHeight}px` }"
  />
</template>
